// Sin créditos en el Chat (ADR-2026-10-12-sin-creditos-en-el-chat). Mismo patrón que los
// videos fuera del plan (`videosSegunPlan.ts`): el modelo sabe el saldo ANTES de contestar,
// así no arma una propuesta que después choca, y una herramienta silenciosa cuelga del
// mensaje el botón «Sumar créditos» (abre el catálogo de planes y créditos de la app).

import type { V2Tool } from '../pages/v2/agentTools';

export const NOMBRE_SUMAR_CREDITOS = 'ofrecer_sumar_creditos';

/** Debajo de esto el saldo se nombra con advertencia y viaja la herramienta del botón. */
export const SALDO_BAJO = 10;

export const HERRAMIENTA_SUMAR_CREDITOS: V2Tool = {
  name: NOMBRE_SUMAR_CREDITOS,
  description:
    'Muestra en el chat el botón «Sumar créditos», que abre los planes y paquetes de créditos. Usala cuando lo que pide cuesta más créditos de los que le quedan. No cuesta nada y no escribe texto: el texto lo ponés vos, en una o dos frases.',
  input_schema: { type: 'object', properties: {} },
};

/**
 * La herramienta del botón para este turno. Con facturación por marca el botón abre el plan de la
 * marca activa (ADR-2026-10-28-elegir-plan-de-marca), no el catálogo de la cuenta: la descripción
 * lo dice para que el modelo no prometa "paquetes". Sin bandera, la de siempre (el mismo objeto).
 */
export function herramientaSumarCreditos(facturaPorMarca: boolean): V2Tool {
  if (!facturaPorMarca) return HERRAMIENTA_SUMAR_CREDITOS;
  return {
    ...HERRAMIENTA_SUMAR_CREDITOS,
    description:
      'Muestra en el chat el botón «Ver el plan», que abre el plan de la marca activa (cada marca de esta cuenta paga su plan y tiene su saldo): ahí el admin lo contrata o lo sube de nivel. Usala cuando lo que pide cuesta más créditos de los que le quedan a esta marca. No cuesta nada y no escribe texto: el texto lo ponés vos, en una o dos frases.',
  };
}

/** ¿Viaja la herramienta del botón? Sólo con el saldo conocido y bajo. */
export function ofreceSumarCreditos(restantes: number | null): boolean {
  return restantes !== null && restantes < SALDO_BAJO;
}

const SIN_PLAN_DE_MARCA =
  ' Ojo: la marca activa todavía no tiene plan propio (ese saldo es sólo el regalo de activación); ' +
  'si se queda sin créditos, decile que contrate el plan de esa marca.';

/** Lo que va al volátil del turno. Vacío si el saldo no se pudo leer. */
export function avisoDeSaldo(restantes: number | null, sinPlanDeMarca = false, facturaPorMarca = false): string {
  if (restantes === null) return '';
  // Facturación por marca (ADR-2026-10-27-facturacion-por-marca): la marca activa no tiene plan
  // propio, sólo el regalo de activación. Se dice, y el resto sigue igual.
  if (sinPlanDeMarca) return avisoDeSaldo(restantes, false, facturaPorMarca) + SIN_PLAN_DE_MARCA;
  // Con la bandera el saldo es de la marca activa, y el botón abre SU plan
  // (ADR-2026-10-28-elegir-plan-de-marca).
  const quien = facturaPorMarca ? 'esta marca' : 'esta cuenta';
  const n = Math.max(0, Math.floor(restantes * 100) / 100);
  if (n < 1) {
    return (
      `\n\nCRÉDITOS: a ${quien} le quedan ${n} créditos: no alcanza para ninguna imagen ni video. ` +
      'Si pide algo que cobra: NO digas que lo vas a hacer, NO armes la propuesta ni la tarjeta. ' +
      `Decí en una o dos frases que se quedó sin créditos, llamá a ${NOMBRE_SUMAR_CREDITOS} para mostrar el botón, ` +
      'y seguí ayudando con lo que no cobra (ideas, textos, planear la sesión).'
    );
  }
  if (n < SALDO_BAJO) {
    return (
      `\n\nCRÉDITOS: a ${quien} le quedan ${n} créditos. Si lo que pide cuesta más que eso, decíselo ANTES de proponer ` +
      `(con el costo), llamá a ${NOMBRE_SUMAR_CREDITOS} y ofrecé una versión que entre en el saldo (menos imágenes, otra calidad).`
    );
  }
  return `\n\nCRÉDITOS: le quedan ${n}.`;
}

/**
 * El texto de la tarjeta «Sumar créditos». Con facturación por marca el saldo y el plan son
 * de la marca activa, y el botón abre SU plan (ADR-2026-10-28-elegir-plan-de-marca).
 */
export function copyDeSumarCreditos(facturaPorMarca: boolean): { titulo: string; detalle: string; boton: string } {
  return facturaPorMarca
    ? { titulo: 'Esta marca se quedó sin créditos', detalle: 'Elegí o subí el plan de esta marca para seguir', boton: 'Ver el plan' }
    : { titulo: 'Te quedaste sin créditos', detalle: 'Sumá créditos o pasate de plan para seguir', boton: 'Sumar créditos' };
}
